import auth_clients from './auth_clients.json'
import priceLabels_D41J from './price_labels_D41J.json'
import products_D41J_07 from './scanned_products_D41J_07.json'
import products_D41J_08 from './scanned_products_D41J_08.json'
import products_D551 from './scanned_products_D551.json'
import products_V173_12 from './scanned_products_V173_12.json'
import shelves_BNT from './scanned_shelves_BNT.json'
import shelves_BC from './scanned_shelves_BuyCountry.json'
import shelves_D1JD from './scanned_shelves_D1JD.json'
import shelves_D41J_07 from './scanned_shelves_D41J_07.json'
import shelves_D41J_08 from './scanned_shelves_D41J_08.json'
import shelves_D4C9 from './scanned_shelves_D4C9.json'
import shelves_D551 from './scanned_shelves_D551.json'
import shelves_V173_12 from './scanned_shelves_V173_12.json'
import scansBNT from './scans_BNT.json'
import scansBC from './scans_BuyCountry.json'
import scansD1JD from './scans_D1JD.json'
import scansD41J from './scans_D41J.json'
import scansD4C9 from './scans_D4C9.json'
import scansD551 from './scans_D551.json'
import scansV173 from './scans_V173.json'
import allStores from './stores.json'
import tasks_D41J_07 from './tasks_D41J_07.json'
import tasks_D41J_08 from './tasks_D41J_08.json'
import tasks_D551 from './tasks_D551.json'
import tasks_V173_12 from './tasks_V173_12.json'
import allUsers from './users.json'

import type { ProductDTO } from '@state/product'
import type { ScanDTO } from '@state/scans'
import type { LayerDTO, ShelfDTO } from '@state/shelf/types'
import type { AuthClientsDTO } from '@state/signInClients'
import type { StoreDTO } from '@state/store'
import type { TaskItemResponse } from '@state/todoList.ts'
import type { StoreRole, UserDTO } from '@state/user'

export interface CustomUser extends Omit<UserDTO, 'storeGrants'> {
    storeGrants: { storeId: string; role: StoreRole }[]
}

const authClients: AuthClientsDTO = auth_clients

const users = allUsers as CustomUser[]

// Base store data
const stores: StoreDTO[] = allStores

const STORE_IDS = {
  D41J: '8501209d-2281-4b65-8c01-1d2f36f251f0',
  V173: 'fff1209d-2281-4b65-8c01-1d2f36f25122',
  D551: 'bde1209d-2281-4b65-8c01-1d2f36f251f0',
  D4C9: '019b1203-5d29-757a-8d50-a5d341ab0850',
  D1JD: '019b1201-1a19-751a-8d10-a5d341ab0850',
  BNT: '019b1201-1a19-751a-8d10-a5d341ab5555',
  BC: '019b1201-1a19-751a-8d10-a5d341bc6666',
}

// Scans for each store
const scansByStoreId: Record<string, ScanDTO[]> = {
  [STORE_IDS.D41J]: scansD41J,
  [STORE_IDS.V173]: scansV173,
  [STORE_IDS.D551]: scansD551,
  [STORE_IDS.D4C9]: scansD4C9,
  [STORE_IDS.D1JD]: scansD1JD,
  [STORE_IDS.BNT]: scansBNT,
  [STORE_IDS.BC]: scansBC,
}

const SCAN_IDS = {
  D41J_07: 'ef654321-1234-4abc-9def-2a1b2c3d4e5f',
  D41J_08: '12345678-2281-4b65-8c01-1d2f36f251f0',
  V173_12: '4cc16059-e8c2-4e22-b618-c5ee09fe45e2',
  D551_07: 'ef654111-1234-4abc-9def-2a1b2c3d4e5f',
  D551_10: '12345678-2281-4b65-8899-1d2f36f251f0',
  D4C9_1: '019b1203-5d28-73b3-849d-375144db5ab8',
  D1JD_1: '012b1203-3d15-71b2-819a-315043dc5bd8',
  BNT: '122a1203-3d26-71b2-819a-315022dc4cd7',
  BC: '122a1203-3d26-71b2-819a-124131fe1ac6',
}

// Sample products for scans
const productsByScanId: Record<string, ProductDTO[]> = {
  [SCAN_IDS.D41J_08]: products_D41J_08 as ProductDTO[],
  [SCAN_IDS.D41J_07]: products_D41J_07 as ProductDTO[],
  [SCAN_IDS.V173_12]: products_V173_12 as ProductDTO[],
  [SCAN_IDS.D551_07]: products_D551 as ProductDTO[],
  [SCAN_IDS.D551_10]: products_D551 as ProductDTO[],
}

// The shelf mock files predate price labels. The D41J-based ones (D41J, D551, V173) get the real
// labels of that scan, taken from resources/D41J_20250129_1.xml; all other layers get none.
type MockLayer = Omit<LayerDTO, 'priceLabels'> & Partial<Pick<LayerDTO, 'priceLabels'>>
type MockShelf = Omit<ShelfDTO, 'layers'> & { layers: MockLayer[] }

const priceLabelsD41J: Record<string, LayerDTO['priceLabels'] | undefined> = priceLabels_D41J

// Sample shelves for scans
const mockShelvesByScanId: Record<string, MockShelf[]> = {
  [SCAN_IDS.D41J_07]: shelves_D41J_07,
  [SCAN_IDS.D41J_08]: shelves_D41J_08,
  [SCAN_IDS.V173_12]: shelves_V173_12,
  [SCAN_IDS.D551_07]: shelves_D551,
  [SCAN_IDS.D551_10]: shelves_D551,
  [SCAN_IDS.D4C9_1]: shelves_D4C9,
  [SCAN_IDS.D1JD_1]: shelves_D1JD,
  [SCAN_IDS.BNT]: shelves_BNT,
  [SCAN_IDS.BC]: shelves_BC,
}

const shelvesByScanId: Record<string, (ShelfDTO & { layers: LayerDTO[] })[]> = Object.fromEntries(
  Object.entries(mockShelvesByScanId).map(([scanId, shelves]) => [
    scanId,
    shelves.map(shelf => ({
      ...shelf,
      layers: shelf.layers.map(layer => ({
        ...layer,
        priceLabels: layer.priceLabels ?? priceLabelsD41J[layer.id] ?? [],
      })),
    })),
  ])
)

const tasksByScanId = {
  [SCAN_IDS.D41J_07]: tasks_D41J_07,
  [SCAN_IDS.D41J_08]: tasks_D41J_08,
  [SCAN_IDS.D551_07]: tasks_D551,
  [SCAN_IDS.V173_12]: tasks_V173_12,
} as Record<string, TaskRaw[]>

interface TaskRaw {
  id: string
  gtin: string
  action: string
  stockLevel: string
  shelfId: string
  isNew: boolean
  status?: 'OPEN' | 'COMPLETED'
}

// Individual exports for backward compatibility
export { authClients, users, stores, scansByStoreId, productsByScanId, shelvesByScanId }

export const getUserByMail = (email: string) =>
  users.find(u => u.email === email)

// Helper functions for easy data access
export const getStoreById = (storeId: string) =>
  stores.find(s => s.id === storeId)

export const getScansForStore = (storeId: string) =>
  scansByStoreId[storeId] ?? []

export const getScanById = (storeId: string, scanId: string) =>
  scansByStoreId[storeId]?.find(s => s.id === scanId)

export const getProductsForScan = (scanId: string) =>
  productsByScanId[scanId] ?? []

export const getShelvesForScan = (scanId: string) =>
  shelvesByScanId[scanId] ?? []

// like the backend: price labels only come with a single shelf, not with the layout
export const withoutPriceLabels = (shelf: ShelfDTO): ShelfDTO => ({
  ...shelf,
  layers: shelf.layers.map(layer => ({ ...layer, priceLabels: [] })),
})

export const getShelfById = (scanId: string, shelfId: string) =>
  shelvesByScanId[scanId]?.find(s => s.id === shelfId)

export const getTasksByScanId = (scanId: string): TaskItemResponse[] => {
  const products = getProductsForScan(scanId)
  const tasks: TaskRaw[] = tasksByScanId[scanId] ?? []

  return tasks.map((raw) => {
    const { status, action, gtin, stockLevel, ...rest } = raw
    const product = products.find(p => p.gtin === gtin)!

    const task: TaskItemResponse = {
      ...rest,
      product,
      stockLevel: stockLevel as TaskItemResponse['stockLevel'],
      action: action as TaskItemResponse['action'],
      status: status ?? 'OPEN',
    }

    return task
  })
}

export const updateTaskStatus = (scanId: string, taskId: string, status: 'OPEN' | 'COMPLETED'): boolean => {
  const tasks = tasksByScanId[scanId]!

  const task = tasks.find(p => p.id === taskId)!
  task.status = status

  return true
}
